import {
  forwardRef,
  ReactElement,
  Ref,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from 'react';
import get from 'lodash/get';
import {
  Table as ReactTable,
  ColumnOrderState,
  ExpandedState,
  PaginationState,
  SortingState,
  getCoreRowModel,
  getExpandedRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  Row,
  RowSelectionState
} from '@tanstack/react-table';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { useAtom, useSetAtom } from 'jotai';
import { Empty, EmptyImage, Spin } from 'Components/Primitives';
import { nonNullable, uniqFast } from 'Utils/functional';
import { TableAction, TableColumn, TableView } from 'Components/Table/Table.types';
import {
  TableDataAtomFam,
  TableColumnSelectAtomFam,
  TableColumnOrderAtomFam,
  TableViewAtomFam
} from 'Components/Table/TableAtoms';
import { useTableColumnsDefs } from 'Components/Table/hooks/useTableColumnDef';
import useTableScrollListeners from 'Components/Table/hooks/useTableScrollListeners';
import { StyledTable } from 'Components/Table/StyledTable';
import { TableHead } from 'Components/Table/Table/TableHead/TableHead';
import { TableBody } from 'Components/Table/Table/TableBody/TableBody';
import { Pagination } from 'Components/Table/Table/Pagination/Pagination';
import { DEFAULT_TABLE_PAGE_SIZE } from 'Components/Table/Table.consts';

export interface TableProps<T> {
  tableId: string;
  data: T[] | undefined;
  columns: TableColumn<T>[];
  rowIdAccessor?: string[];
  initialTableView?: TableView;
  actions?: TableAction<T>[];
  enablePagination?: boolean;
  enableColumnRemove?: boolean;
  // resets page to 1 when data changes
  autoResetPageIndex?: boolean;
  loading?: boolean;
  className?: string;
  'data-testid'?: string;
  onChange?(tableView: TableView): void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getCellClassNames?(value: any, colId: string): string[];
  onRowsSelectChange?(ids: string[]): void;
  onExpandRow?(row: T): ReactElement;
  enableRowSelection?(row: T): boolean;
}

export interface TableRefHandle {
  resetTableSelection: () => void;
}

export const Table = forwardRef(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  <T extends { [key: string]: any; subRows?: T[] }>(
    {
      tableId,
      data,
      columns,
      rowIdAccessor,
      initialTableView,
      actions,
      className,
      enablePagination = true,
      enableColumnRemove = true,
      autoResetPageIndex = true,
      loading,
      'data-testid': dataTestId,
      onChange,
      getCellClassNames,
      onRowsSelectChange,
      onExpandRow,
      enableRowSelection
    }: TableProps<T>,
    ref: Ref<TableRefHandle>
  ) => {
    const { t } = useTranslation(['common']);
    const [dataRecoil] = useAtom(TableDataAtomFam({ key: tableId }));
    const [columnSelectedRecoil, setColumnSelectedRecoil] = useAtom(TableColumnSelectAtomFam({ key: tableId }));
    const [columnOrderRecoil] = useAtom(TableColumnOrderAtomFam({ key: tableId }));
    const setTableViewRecoil = useSetAtom(TableViewAtomFam({ key: tableId }));
    const [tableData, setTableData] = useState<T[] | undefined>();
    const [colIds, setColIds] = useState<string[]>([]);
    const [expanded, setExpanded] = useState<ExpandedState>({});
    const [columnOrder, setColumnOrder] = useState<ColumnOrderState>([]);
    const [columnVisibility, setColumnVisibility] = useState(initialTableView?.selectedColumns ? {} : {});
    const [sorting, setSorting] = useState<SortingState>(
      initialTableView?.sorter
        ? [{ id: initialTableView.sorter.columnKey, desc: initialTableView.sorter.order === 'descend' }]
        : []
    );
    const [pagination, setPagination] = useState<PaginationState>({
      pageIndex: initialTableView?.pagination?.current || 0,
      pageSize: enablePagination ? initialTableView?.pagination?.pageSize || DEFAULT_TABLE_PAGE_SIZE : 1000000
    });
    const [rowSelection, setRowSelection] = useState<{ [key: string]: boolean }>({});

    const table = useRef<ReactTable<T>>(null);
    const tableRef = useRef<HTMLDivElement>(null);

    useImperativeHandle(
      ref,
      () => ({
        resetTableSelection: () => table.current?.resetRowSelection()
      }),
      [table]
    );

    const withRowSelect = useMemo((): boolean => !!onRowsSelectChange, [onRowsSelectChange]);

    const onColumnRemove = useCallback(
      (colId: string) => {
        if (!enableColumnRemove) return;
        setColumnSelectedRecoil((curSelected) => ({ ids: (curSelected?.ids || []).filter((id) => id !== colId) }));
      },
      [enableColumnRemove, setColumnSelectedRecoil]
    );

    const getTableSortingState = useCallback((): SortingState | undefined => table.current?.getState().sorting, []);

    const getRowCanExpand = useCallback(
      (row: Row<T>): boolean => typeof onExpandRow === 'function' || (row.subRows || []).length > 0,
      [onExpandRow]
    );

    const enableRowSelectionFn = useMemo(
      (): ((row: Row<T>) => boolean) | null =>
        typeof enableRowSelection === 'function' ? (row: Row<T>) => enableRowSelection(row.original) : null,
      [enableRowSelection]
    );

    const colDefParams = useMemo((): {
      columns: TableColumn<T>[];
      actions: TableAction<T>[] | undefined;
      withRowSelect: boolean;
      getTableSortingState(): SortingState | undefined;
    } => {
      return {
        columns,
        actions,
        withRowSelect,
        getTableSortingState
      };
    }, [columns, actions, withRowSelect, getTableSortingState]);
    const columnsDef = useTableColumnsDefs(colDefParams);

    const { classList } = useTableScrollListeners(tableRef);

    const pinnedLeft = useMemo(
      (): string[] => ['row-select', ...columns.filter((col) => col.pinned === 'left').map((col) => col.id)],
      [columns]
    );

    const pinnedRight = useMemo(
      (): string[] => [...columns.filter((col) => col.pinned === 'right').map((col) => col.id), 'actions'],
      [columns]
    );

    const isEmpty = useMemo((): boolean => (tableData && tableData.length === 0 ? true : false), [tableData]);

    table.current = useReactTable<T>({
      data: tableData || [],
      columns: columnsDef,
      state: { expanded, sorting, columnOrder, pagination, columnVisibility, rowSelection },
      sortDescFirst: false,
      paginateExpandedRows: false,
      autoResetPageIndex,
      getSubRows: (row) => row.subRows,
      onColumnOrderChange: setColumnOrder,
      onSortingChange: setSorting,
      onExpandedChange: setExpanded,
      onPaginationChange: setPagination,
      onRowSelectionChange: setRowSelection,
      onColumnVisibilityChange: setColumnVisibility,
      getRowCanExpand,
      getCoreRowModel: getCoreRowModel(),
      getExpandedRowModel: getExpandedRowModel(),
      getSortedRowModel: getSortedRowModel(),
      getPaginationRowModel: getPaginationRowModel(),
      ...(enableRowSelectionFn && { enableRowSelection: enableRowSelectionFn }),
      ...(rowIdAccessor && { getRowId: (row) => get(row, rowIdAccessor) })
    });

    const emitTableChange = useCallback(
      (params?: { paginationState?: PaginationState }) => {
        const paginationState = params?.paginationState || table.current?.getState().pagination;
        const sortingState = table.current?.getState().sorting;
        const columnOrderState = table.current?.getState().columnOrder;

        const tableView: TableView = {
          ...(paginationState && {
            pagination: { pageSize: paginationState.pageSize, current: paginationState.pageIndex }
          }),
          ...(sortingState &&
            sortingState.length > 0 && {
              sorter: { columnKey: sortingState[0].id, order: sortingState[0].desc ? 'descend' : 'ascend' }
            }),
          ...(columnOrderState && { columnsOrder: columnOrderState }),
          ...(columnSelectedRecoil && { selectedColumns: columnSelectedRecoil.ids })
        };

        onChange && onChange(tableView);
        setTableViewRecoil(tableView);
      },
      [table, columnSelectedRecoil, onChange, setTableViewRecoil]
    );

    const onPaginationChange = useCallback(
      (paginationState: PaginationState) => {
        emitTableChange({ paginationState });
      },
      [emitTableChange]
    );

    // initialize column ids
    // reset sorting state if sorting column is removed
    useEffect(() => {
      const changedColIds = columns.map((col) => col.id as string);
      setColIds(changedColIds);
      const sortColId = table?.current?.getState().sorting[0]?.id;
      if (sortColId && !changedColIds.includes(sortColId)) {
        setSorting([]);
      }
    }, [columns]);

    // update column order from recoil
    useEffect(() => {
      const orderRecoil = (columnOrderRecoil?.ids || []).filter(nonNullable);
      const orderIds = orderRecoil && orderRecoil.length > 0 ? orderRecoil : columns.map((col) => col.id as string);
      const idsAndRowSelect = withRowSelect
        ? ['row-select', ...orderIds]
        : orderIds.filter((id) => id !== 'row-select');
      setColumnOrder(uniqFast(idsAndRowSelect));
    }, [columnOrderRecoil, withRowSelect, columns]);

    // update table data when data updates through recoil (can be updated via recoil in tableControl)
    useEffect(() => {
      setTableData(dataRecoil || data || []);
      autoResetPageIndex && setPagination((curPagination) => ({ ...curPagination, pageIndex: 0 }));
    }, [autoResetPageIndex, data, dataRecoil]);

    // update table select when data changes
    useEffect(() => {
      if (!data || !rowIdAccessor || !table.current) return;
      const dataIds = data?.map((row) => get(row, rowIdAccessor)).map((v) => v.toString());
      const selected = table.current.getState().rowSelection;
      setRowSelection(
        Object.keys(selected).reduce((acc: RowSelectionState, key) => {
          if (dataIds.includes(key)) {
            acc[key] = selected[key];
          }
          return acc;
        }, {})
      );
    }, [table, rowIdAccessor, data]);

    // update column visibility when column selected changes
    useEffect(() => {
      if (!colIds) return;
      setColumnVisibility(
        colIds.reduce(
          (acc, id) => ({ ...acc, [id]: columnSelectedRecoil?.ids ? columnSelectedRecoil.ids.includes(id) : true }),
          {}
        )
      );
    }, [colIds, columnSelectedRecoil]);

    // onChange emitter state -> sorting change
    useEffect(() => {
      emitTableChange();
    }, [sorting, emitTableChange]);

    // onChange emitter state -> column order change
    useEffect(() => {
      emitTableChange();
    }, [columnOrder, emitTableChange]);

    // onRowSelect emitter -> row select change
    useEffect(() => {
      if (!onRowsSelectChange || !rowSelection) return;
      onRowsSelectChange(Object.keys(rowSelection).filter((key) => rowSelection[key]));
    }, [rowSelection, onRowsSelectChange]);

    return (
      <StyledTable
        ref={tableRef}
        data-testid={dataTestId}
        withRowSelect={withRowSelect}
        className={classNames(className, loading && 'loading', isEmpty && 'empty', classList)}
      >
        <div className="table-wrapper">
          <table>
            <TableHead
              table={table.current}
              pinnedLeft={pinnedLeft}
              pinnedRight={pinnedRight}
              sortingState={sorting}
              enableColumnRemove={enableColumnRemove}
              onColumnRemove={onColumnRemove}
            />
            <TableBody
              table={table.current}
              pinnedLeft={pinnedLeft}
              pinnedRight={pinnedRight}
              onExpandRow={onExpandRow}
              getCellClassNames={getCellClassNames}
            />
          </table>
        </div>
        {isEmpty && <Empty className="empty-data-icon" image={EmptyImage.Empty} description={t('no-data')} />}
        {loading && <Spin className="table-loading-spinner" />}

        <div className="table-footer">
          {enablePagination && <Pagination table={table.current} onChange={onPaginationChange} />}
        </div>
      </StyledTable>
    );
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
) as <T extends { [key: string]: any; subRows?: T[] }>(
  props: TableProps<T> & { ref?: Ref<TableRefHandle> }
) => ReactElement | null;
